// src/pages/ConvertFormulaPage.jsx
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import "./ConvertFormulaPage.css";

function toPSLText(item) {
  let dateObj;
  if (item.createdAt?.toDate) {
    dateObj = item.createdAt.toDate();
  } else if (typeof item.createdAt === "string" || typeof item.createdAt === "number") {
    dateObj = new Date(item.createdAt);
  } else if (item.createdAt instanceof Date) {
    dateObj = item.createdAt;
  } else {
    dateObj = new Date();
  }
  const createdAt = dateObj.toLocaleString("en-GB");
  const creatorEmail = item.creatorEmail || "";

  const headerLines = [
    `NAME: ${item.name || ""}`,
    `CODE: ${item.code || ""}`,
    `PRODUCT: ${item.product || ""}`,
    `BASE: ${item.base || ""}`,
    `SIZE_L: ${item.sizeL || ""}`,
    `BASE_TYPE: ${item.baseType || ""}`,
    `CONFIDENCE: ${item.confidence || ""}`,
    item.notes ? `NOTES: ${item.notes}` : null,
    `CREATED_AT: ${createdAt}`,
    `CREATOR_EMAIL: ${creatorEmail}`,
  ].filter(Boolean);

  const rowsLines = (item.rows || [])
    .filter((r) => r.colorant?.trim() && String(r.value ?? "").trim())
    .map((r) => `${r.colorant.trim()} = ${Number(r.value || 0)}`);

  return [...headerLines, "------", "Formula:", ...rowsLines].join("\n");
}

function convertFormula(item, newSizeL) {
  if (!item || !item.rows) return item;
  const oldSize = Number(item.sizeL || 0);
  const newSize = Number(newSizeL);
  if (!oldSize || !newSize) return item;
  const factor = newSize / oldSize;
  return {
    ...item,
    sizeL: newSize,
    rows: item.rows.map((r) => ({
      ...r,
      value: r.value ? +(parseFloat(r.value) * factor).toFixed(1) : r.value,
    })),
  };
}

const blankManualRows = Array(8)
  .fill(0)
  .map(() => ({ colorant: "", value: "" }));

export default function ConvertFormulaPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFormula = location.state?.formula || null;

  const [manualRows, setManualRows] = useState(blankManualRows);
  const [desiredSize, setDesiredSize] = useState(
    initialFormula?.sizeL !== undefined && initialFormula?.sizeL !== null
      ? String(initialFormula.sizeL)
      : ""
  );
  const [converted, setConverted] = useState(null);
  const [showLeftBox, setShowLeftBox] = useState(true); // ✅ universal toggle

  if (!initialFormula) {
    return (
      <div className="app">
        <Header title="CCM Tinting Studio" />
        <main style={{ maxWidth: 540, margin: "60px auto" }}>
          <h2>No formula selected for conversion.</h2>
          <button className="product-card-box__btn" onClick={() => navigate("/search")}>
            Back to Search
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <Header title="CCM Tinting Studio" />
      <main className="convert-main-wide">
        <div className="convert-menu-thick" style={{ alignItems: "center" }}>
          <b>
            {initialFormula.legacy
              ? "Manual Conversion for Legacy Formulas"
              : "Convert Formula"}
          </b>

          {/* ✅ Toggle button for both modes */}
          <button
            className="product-card-box__btn"
            style={{
              marginLeft: 10,
              background: "#444",
              color: "#fff",
              fontWeight: 600,
            }}
            onClick={() => setShowLeftBox(!showLeftBox)}
          >
            {showLeftBox ? "Hide Left Box" : "Show Left Box"}
          </button>

          {!initialFormula.legacy && (
            <>
              <div style={{ marginLeft: 20 }}>
                <b>Convert to Size (L):</b>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={desiredSize}
                  onChange={(e) => setDesiredSize(e.target.value)}
                  className="convert-size-input"
                />
              </div>
              <button
                className="product-card-box__btn"
                type="button"
                onClick={(e) => {
                  e?.preventDefault?.();
                  if (!desiredSize || isNaN(Number(desiredSize)) || Number(desiredSize) <= 0) {
                    setConverted(null);
                    return;
                  }
                  setConverted(convertFormula(initialFormula, Number(desiredSize)));
                }}
                style={{ marginLeft: 12, fontWeight: 700, fontSize: "1.08em" }}
              >
                Convert
              </button>
              <span style={{ color: "#999", marginLeft: 20, fontSize: "0.98em" }}>
                Original size: <b>{initialFormula.sizeL}</b> L
              </span>
            </>
          )}

          <span style={{ flex: 1 }} />
          <button
            className="product-card-box__btn"
            style={{ marginLeft: 10 }}
            onClick={() => navigate("/search")}
          >
            Back to Search
          </button>
        </div>

        {initialFormula.legacy ? (
          <div className="convert-row-flex">
            {/* LEFT LEGACY BOX */}
            {showLeftBox && (
              <div
                className="convert-formula-box-thick"
                style={{
                  color: "#111",
                  fontSize: "1.6em",
                  transition: "all 0.25s ease-in-out",
                }}
              >
                <h2 className="convert-formula-title" style={{ color: "#111" }}>
                  {initialFormula.name || "Legacy Formula"}
                </h2>
                <pre
                  style={{
                    background: "#fff",
                    border: "1px solid #eee",
                    borderRadius: 7,
                    padding: "12px 14px",
                    fontSize: "1em",
                    color: "#111",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {Object.entries(initialFormula)
                    .map(([key, value]) => {
                      if (typeof value === "undefined" || value === "" || value === null)
                        return null;
                      let displayVal = value;
                      if (typeof value === "object") {
                        try {
                          displayVal = JSON.stringify(value, null, 2);
                        } catch {
                          displayVal = String(value);
                        }
                      }
                      return `${key}: ${displayVal}\n`;
                    })
                    .join("")}
                </pre>
              </div>
            )}

            {/* RIGHT: Manual entry form */}
            <div
              className="convert-formula-box-thick"
              style={{
                color: "#111",
                flex: showLeftBox ? "1" : "100%",
                transition: "all 0.25s ease-in-out",
              }}
            >
              <h2 className="convert-formula-title" style={{ color: "#111" }}>
                Manual Converted Formula
              </h2>
              <table
                style={{
                  width: "100%",
                  marginBottom: 10,
                  borderCollapse: "collapse",
                  marginTop: 6,
                  fontSize: "1.09em",
                  color: "#111",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", color: "#555", padding: "4px 7px" }}>
                      Colourant
                    </th>
                    <th style={{ textAlign: "left", color: "#555", padding: "4px 7px" }}>
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {manualRows.map((row, i) => (
                    <tr key={i}>
                      <td style={{ padding: "4px 7px", border: "none" }}>
                        <input
                          type="text"
                          value={row.colorant}
                          onChange={(e) => {
                            const next = manualRows.slice();
                            next[i] = { ...next[i], colorant: e.target.value };
                            setManualRows(next);
                          }}
                          style={{
                            width: "98%",
                            padding: "6px",
                            fontSize: "1em",
                            border: "1px solid #bbb",
                            borderRadius: 5,
                            color: "#111",
                            background: "#fff",
                            marginBottom: 0,
                            boxSizing: "border-box",
                          }}
                          placeholder=""
                        />
                      </td>
                      <td style={{ padding: "4px 7px", border: "none" }}>
                        <input
                          type="text"
                          value={row.value}
                          onChange={(e) => {
                            const next = manualRows.slice();
                            next[i] = { ...next[i], value: e.target.value };
                            setManualRows(next);
                          }}
                          style={{
                            width: "98%",
                            padding: "6px",
                            fontSize: "1em",
                            border: "1px solid #bbb",
                            borderRadius: 5,
                            color: "#111",
                            background: "#fff",
                            marginBottom: 0,
                            boxSizing: "border-box",
                          }}
                          placeholder=""
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="convert-row-flex">
            {/* LEFT BOX (Original Formula) */}
            {showLeftBox && (
              <div className="convert-formula-box-thick" style={{ color: "#111" }}>
                <h2 className="convert-formula-title" style={{ color: "#111" }}>
                  {initialFormula.name || "(No name)"}
                </h2>
                <div style={{ marginBottom: 10 }}>
                  <b>Size (L):</b> {initialFormula.sizeL}
                  <span style={{ margin: "0 10px", color: "#888" }}>|</span>
                  <b>Product:</b> {initialFormula.product || "-"}
                  <span style={{ margin: "0 10px", color: "#888" }}>|</span>
                  <b>Base:</b> {initialFormula.base || "-"}
                </div>
                {initialFormula.notes && (
                  <div className="convert-formula-note">{initialFormula.notes}</div>
                )}
                <div>
                  <div className="convert-formula-section-label" style={{ color: "#111" }}>
                    Original Formula:
                  </div>
                  <pre
                    className="product-card-box__psl"
                    style={{ marginBottom: 10, color: "#111" }}
                  >
                    {toPSLText(initialFormula)}
                  </pre>
                </div>
              </div>
            )}

            {/* RIGHT BOX (Converted Formula) */}
            <div
              className="convert-formula-box-thick"
              style={{
                color: "#111",
                flex: showLeftBox ? "1" : "100%",
                transition: "all 0.25s ease-in-out",
              }}
            >
              <h2 className="convert-formula-title" style={{ color: "#111" }}>
                Converted Formula
              </h2>
              <div>
                <div className="convert-formula-section-label" style={{ color: "#111" }}>
                  Converted:
                </div>
                <pre
                  className="product-card-box__psl"
                  style={{ marginBottom: 0, color: "#111" }}
                >
                  {converted
                    ? toPSLText(converted)
                    : "(Enter a valid new size and click Convert)"}
                </pre>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
